<template>
    <div>
        <p>{{ t('choose_route') }}</p>
        <AccordionList :disabled="isDesktop">
            <AccordionItem
                v-for="service in servicesTypes"
                :key="`service-${service.code}`"
                :name="`routes-${service.class}`"
                class="route-type"
                :class="service.class"
            >
                <template #title="{ isDisabled }">
                    <div class="service-wrapper">
                        <div class="service-infos">
                            <img
                                class="icon-service"
                                :src="service.icon"
                                width="40"
                                height="40"
                                aria-hidden="true"
                                alt=""
                            />
                            <div>
                                <h2 class="service-title">{{ service.name }}</h2>
                                <div class="service-description">{{ service.description }}</div>
                            </div>
                        </div>
                        <div v-if="!isDisabled" class="dropdown-arrow-wrapper">
                            <IconDropdownArrow width="16" height="9" />
                        </div>
                    </div>
                </template>
                <template #content>
                    <div class="route-list" :class="service.class">
                        <div v-if="service.details" class="service-description details">
                            {{ service.details }}
                        </div>
                        <ul class="no-bullet">
                            <li v-for="route in service.routes" :key="`route-${route.noParcours}`" class="route-item">
                                <RouterLink
                                    :to="{
                                        name: 'route-single',
                                        params: {
                                            route: route.noParcours,
                                            direction: route.descriptionDirectionPrincipale
                                                ? directionSlug(route.codeDirectionPrincipale, route.descriptionDirectionPrincipale)
                                                : directionSlug(route.codeDirectionRetour, route.descriptionDirectionRetour),
                                        },
                                    }"
                                >
                                    <span class="visuallyhidden">{{ t('route') }}&#32;</span>
                                    {{ route.noParcours }}
                                </RouterLink>
                            </li>
                        </ul>
                        <div
                            v-for="combinedService in getCombinedTypes(service.code)"
                            :key="`combined-service-${combinedService.code}`"
                        >
                            <div class="combined-service-wrapper">
                                <h3 class="service-title">{{ combinedService.name }}</h3>
                                <div class="combined-service-message">
                                    <img class="alert-icon" src="@@/img/alert.svg" width="19" height="19" alt="" />
                                    <div class="service-description">
                                        {{ combinedService.description }}
                                        <template v-if="combinedService.code === 2">
                                            <br />
                                            <a :href="t('routes_by_school_link')">
                                                {{ t('routes_by_school') }}
                                            </a>
                                        </template>
                                    </div>
                                </div>
                            </div>
                            <ul class="no-bullet">
                                <li
                                    v-for="route in combinedService.routes"
                                    :key="`route-${route.noParcours}`"
                                    class="route-item"
                                >
                                    <RouterLink
                                        :to="{
                                            name: 'route-single',
                                            params: {
                                                route: route.noParcours,
                                                direction: route.descriptionDirectionPrincipale
                                                ? directionSlug(route.codeDirectionPrincipale, route.descriptionDirectionPrincipale)
                                                : directionSlug(route.codeDirectionRetour, route.descriptionDirectionRetour),
                                            },
                                        }"
                                    >
                                        <span class="visuallyhidden">{{ t('route') }}&#32;</span>
                                        {{ route.noParcours }}
                                    </RouterLink>
                                </li>
                            </ul>
                        </div>
                    </div>
                </template>
            </AccordionItem>
        </AccordionList>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { useWindowSize, useDebounce } from '@vueuse/core';
import { RouterLink } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';

import { useSchedulesStore } from '@@/js/stores/schedules';
import directionSlug from '@@/js/utils/directionSlug';
import AccordionList from '@/shared/AccordionList';
import AccordionItem from '@/shared/AccordionItem';

import IconDropdownArrow from '@@/icons/dropdown-arrow.svg';

const schedulesStore = useSchedulesStore();
const { routesByServices } = storeToRefs(schedulesStore);

const { t } = useI18n({
    messages: {
        fr: {
            choose_route: 'Choisissez un parcours pour obtenir son horaire',
            route: 'Parcours',
            important_information: 'Information importante',
            routes_by_school: 'Liste des parcours par établissement scolaire',
            routes_by_school_link:
                '/horaires-et-trajets/horaires-des-bus/horaires-imprimer/parcours-par-etablissement-scolaire',
        },
        en: {
            choose_route: 'Choose a route to obtain his schedule',
            route: 'Route',
            important_information: 'Important information',
            routes_by_school: 'Routes by educational institution',
            routes_by_school_link:
                '/en/schedules-and-routes/bus-schedule/printable-schedules/routes-educational-institution',
        },
    },
});

const servicesTypes = computed(() => {
    return routesByServices.value.filter((s) => typeof s.showWith === 'undefined');
});

const { width } = useWindowSize();
const debouncedWidth = useDebounce(width, 100);
const isDesktop = computed(() => debouncedWidth.value > 1024);

function getCombinedTypes(id) {
    return routesByServices.value.filter((s) => s.showWith === id);
}
</script>

<style lang="scss" scoped>
.route-type {
    margin-bottom: 8px;
    @include breakpoint(large) {
        margin-bottom: 16px;
    }
    @include colorize {
        :deep(.accordion-button) {
            position: relative;
            background: get-color-variation('base');
            transition: background-color 300ms ease;
            &:hover {
                background: get-color-variation('hover');
                transition: background-color 300ms ease;
                &.is-open {
                    background: $color-white;
                }
            }
            &.is-open,
            &[disabled] {
                border-bottom: 2px solid $color-black-2;
                border-radius: 20px 20px 0 0;
                background: $color-white;
                &::before {
                    content: '';
                    display: block;
                    position: absolute;
                    bottom: 0;
                    right: 0;
                    left: 0;
                    border-top: 3px solid get-color-variation('base');
                }
                .service-title {
                    color: get-color-variation('base');
                }
            }
            .service-title {
                margin: 0;
            }
        }
    }
}
.service-wrapper {
    padding: 16px;
    color: $color-white;
    display: flex;
    align-items: center;
    justify-content: space-between;
    overflow: hidden;
    .service-title {
        @include rem-typo(20px);
        margin: 0;
    }
}
.service-infos {
    display: flex;
}
.alert-icon {
    margin-right: 8px;
    @include breakpoint(medium) {
        margin-top: 4px;
    }
}
.service-description {
    @include rem-typo(14px);
    .is-open & {
        color: $color-black-50;
    }
    &.details {
        margin-bottom: 16px;
    }
}
.icon-service {
    margin-right: 8px;
}
.dropdown-arrow-wrapper {
    display: flex;
    position: relative;
    width: 30px;
    height: 30px;
    margin-left: 8px;
    border-radius: 30px;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    svg {
        transition: all 300ms ease;
        transform: none;
    }
    .is-open & {
        svg {
            transition: all 300ms ease;
            transform: rotate(180deg);
        }
    }
}
@include colorize {
    .dropdown-arrow-wrapper {
        background-color: get-color-variation('dark');
        .is-open & {
            background-color: get-color-variation('base');
        }
    }
}
.combined-service-wrapper {
    margin: 32px 0 16px;
    .combined-service-message {
        display: flex;
    }
    .service-title {
        @include rem-typo(16px);
        margin: 0 0 8px;
        font-weight: 700;
        @include breakpoint(medium) {
            @include rem-typo(18px);
        }
    }
    .service-description {
        @include rem-typo(14px);
        color: $color-black;
        @include breakpoint(medium) {
            @include rem-typo(16px);
        }
    }
}
.route-list {
    margin: 0;
    padding: 16px 13px 13px 16px;
    background: $color-white;
    border-width: 0 1px 1px;
    border-radius: 0 0 20px 20px;
}
.error-description {
    @include rem-typo(20px);
    margin-bottom: 32px;
    font-weight: 300;
    p {
        margin: 0;
    }
}
.route-item {
    display: inline-block;
    a {
        @include rem-typo(14px);
        display: inline-block;
        position: relative;
        min-width: 40px;
        margin-right: 3px;
        margin-bottom: 3px;
        padding: 8px 0;
        font-weight: 700;
        text-align: center;
        text-decoration: none;
        color: $color-white;
        border-radius: 3px;
        cursor: pointer;
        &:focus,
        &:hover {
            color: $color-white;
        }
        @include breakpoint(medium) {
            @include rem-typo(18px);
            min-width: 64px;
            padding: 10px 0;
        }
    }
}
@include colorize {
    .route-item a {
        background-color: get-color-variation('base');
        &:hover {
            background-color: get-color-variation('hover');
        }
    }
}
</style>
